import React, { useState, useEffect } from 'react';
import Modal from 'components/Modal';
import TextField from 'components/TextField';
import Button from 'components/Button';
import { Container, SearchContainer, ResultsList, ResultItem, LoadingContainer, EmptyMessage } from './styles';

const API_IBGE_MUNICIPIOS = 'https://servicodados.ibge.gov.br/api/v1/localidades/municipios';

function ModalBuscaMunicipio({ open, onClose, onSelect, ufFiltro = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [municipios, setMunicipios] = useState([]);
  const [filteredMunicipios, setFilteredMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setFilteredMunicipios([]);
      setError(null);
      
      // Carregar municípios ao abrir o modal
      if (municipios.length === 0) {
        loadMunicipios();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredMunicipios([]);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    let filtered = municipios;

    // Filtrar por UF se fornecido
    if (ufFiltro && ufFiltro.length === 2) {
      filtered = municipios.filter(m => {
        // A API do IBGE retorna a UF em diferentes estruturas dependendo do endpoint
        // Tentar diferentes caminhos possíveis
        const ufSigla = m.microrregiao?.mesorregiao?.UF?.sigla || 
                       m.microrregiao?.mesorregiao?.Uf?.sigla ||
                       m.microrregiao?.mesorregiao?.uf?.sigla ||
                       m.UF?.sigla ||
                       m.Uf?.sigla ||
                       m.uf?.sigla ||
                       '';
        return ufSigla.toUpperCase() === ufFiltro.toUpperCase();
      });
    }

    // Filtrar por nome
    filtered = filtered.filter(m =>
      m.nome.toLowerCase().includes(term)
    );

    // Limitar a 50 resultados para performance
    setFilteredMunicipios(filtered.slice(0, 50));
  }, [searchTerm, municipios, ufFiltro]);

  const loadMunicipios = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(API_IBGE_MUNICIPIOS);
      if (!response.ok) {
        throw new Error('Erro ao buscar municípios');
      }
      const data = await response.json();
      setMunicipios(data);
    } catch (err) {
      console.error('Erro ao carregar municípios:', err);
      setError('Erro ao carregar lista de municípios. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (municipio) => {
    if (onSelect) {
      // Extrair UF de diferentes estruturas possíveis da API
      const ufSigla = municipio.microrregiao?.mesorregiao?.UF?.sigla || 
                     municipio.microrregiao?.mesorregiao?.Uf?.sigla ||
                     municipio.microrregiao?.mesorregiao?.uf?.sigla ||
                     municipio.UF?.sigla ||
                     municipio.Uf?.sigla ||
                     municipio.uf?.sigla ||
                     '';
      onSelect({
        codigo: municipio.id.toString(),
        nome: municipio.nome,
        uf: ufSigla,
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setFilteredMunicipios([]);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Consultar Município">
      <Container>
        <SearchContainer>
          <TextField
            label="Buscar município por nome"
            name="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            placeholder={ufFiltro ? `Digite o nome do município em ${ufFiltro.toUpperCase()}` : "Digite o nome do município"}
            InputLabelProps={{
              style: { color: '#666' },
            }}
            inputProps={{
              style: { color: '#333' },
            }}
            autoFocus
          />
        </SearchContainer>

        {ufFiltro && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '-10px', marginBottom: '10px' }}>
            Filtrando por UF: <strong>{ufFiltro.toUpperCase()}</strong>
          </div>
        )}

        {loading && (
          <LoadingContainer>
            <div>Carregando municípios...</div>
          </LoadingContainer>
        )}

        {error && (
          <div style={{ color: '#d32f2f', fontSize: '14px', marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        {!loading && !error && searchTerm.trim() !== '' && (
          <>
            {filteredMunicipios.length > 0 ? (
              <>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                  {filteredMunicipios.length} resultado(s) encontrado(s)
                </div>
                <ResultsList>
                  {filteredMunicipios.map((municipio) => {
                    // Extrair UF de diferentes estruturas possíveis da API
                    const ufSigla = municipio.microrregiao?.mesorregiao?.UF?.sigla || 
                                   municipio.microrregiao?.mesorregiao?.Uf?.sigla ||
                                   municipio.microrregiao?.mesorregiao?.uf?.sigla ||
                                   municipio.UF?.sigla ||
                                   municipio.Uf?.sigla ||
                                   municipio.uf?.sigla ||
                                   '';
                    return (
                      <ResultItem
                        key={municipio.id}
                        onClick={() => handleSelect(municipio)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                            {municipio.nome}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {ufSigla ? `UF: ${ufSigla} | ` : ''}Código IBGE: {municipio.id}
                          </div>
                        </div>
                      </ResultItem>
                    );
                  })}
                </ResultsList>
              </>
            ) : (
              <EmptyMessage>
                Nenhum município encontrado com "{searchTerm}"
                {ufFiltro && ` em ${ufFiltro.toUpperCase()}`}
              </EmptyMessage>
            )}
          </>
        )}

        {!loading && !error && searchTerm.trim() === '' && (
          <EmptyMessage>
            Digite o nome do município para buscar
          </EmptyMessage>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
          <Button
            label="Fechar"
            onClick={handleClose}
            color="secondary"
          />
        </div>
      </Container>
    </Modal>
  );
}

export default ModalBuscaMunicipio;

